// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useProjectContext } from '@/project/hooks/use-project-context';
import {
  DemandScheduleRow,
  buildDemandSchedule,
} from '@/project/utilities/build-demand-schedule';
import { buildStepPaths } from '@/project/utilities/demand-schedule-paths';
import { formatNumber } from '@/utilities/number';
import { Box, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { PatternLines } from '@visx/pattern';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Line } from '@visx/shape';
import { DateTime } from 'luxon';
import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const GUTTER_WIDTH = 240;
const CHART_MIN_WIDTH = 360;
const ROW_HEIGHT = 72;
const ROW_GAP = 16;
const AXIS_HEIGHT = 28;
const CHART_PADDING_X = 12;
const MAX_TICKS = 12;

type DateScale = AxisScale<number>;

/**
 * Per-SKU demand schedule tab. Small-multiples of step charts — one row per
 * SKU on the project, sharing an x-axis so operators can compare demand
 * shapes across SKUs. Reads only project line items (no fulfillments).
 */
export function DemandScheduleCard() {
  const { project } = useProjectContext();
  const schedule = useMemo(
    () =>
      buildDemandSchedule(
        project.items ?? [],
        project.startOn?.toMillis() ?? null,
        project.endOn?.toMillis() ?? null
      ),
    [project.items, project.startOn, project.endOn]
  );

  if (schedule.rows.length === 0) {
    return (
      <EmptyState
        heading="No line items yet"
        body="Add line items to this project to see its demand schedule."
      />
    );
  }

  const anyDatedDemand = schedule.rows.some((row) => row.hasDatedDemand);
  if (
    !anyDatedDemand ||
    schedule.rangeStartMs === null ||
    schedule.rangeEndMs === null
  ) {
    return (
      <EmptyState
        heading="This project has only dateless demand"
        body="Constant demand across the project. View totals on the Fulfillment by SKU tab."
      />
    );
  }

  return (
    <DemandScheduleContent
      rows={schedule.rows}
      rangeStartMs={schedule.rangeStartMs}
      rangeEndMs={schedule.rangeEndMs}
    />
  );
}

interface EmptyStateProps {
  heading: string;
  body: string;
}

function EmptyState({ heading, body }: EmptyStateProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        padding: 4,
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" color="text.primary">
        {heading}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ marginTop: 1 }}
      >
        {body}
      </Typography>
    </Paper>
  );
}

interface DemandScheduleContentProps {
  rows: DemandScheduleRow[];
  rangeStartMs: number;
  rangeEndMs: number;
}

function DemandScheduleContent({
  rows,
  rangeStartMs,
  rangeEndMs,
}: DemandScheduleContentProps) {
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredMs, setHoveredMs] = useState<number | null>(null);
  const chartColumnRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    if (chartColumnRef.current) {
      setChartWidth(chartColumnRef.current.clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    measure();
    const target = chartColumnRef.current;
    if (!target) return;
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, [measure]);

  const today = useMemo(() => DateTime.now().startOf('day'), []);

  const axisRange = useMemo(() => {
    const start = DateTime.fromMillis(rangeStartMs);
    // Keep the axis domain on the user-visible project range. The final
    // day-after drop is clamped by the step path builder instead of widening
    // the scale, so tick unit selection stays based on the actual range.
    const candidateEnd = DateTime.fromMillis(rangeEndMs);
    const end = candidateEnd > start ? candidateEnd : start.plus({ days: 1 });
    return { start, end };
  }, [rangeStartMs, rangeEndMs]);

  const tickUnit = useMemo(
    () => pickTickUnit(axisRange.start, axisRange.end),
    [axisRange]
  );

  const effectiveChartWidth = Math.max(chartWidth, CHART_MIN_WIDTH);

  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [CHART_PADDING_X, effectiveChartWidth - CHART_PADDING_X],
        domain: [axisRange.start.toJSDate(), axisRange.end.toJSDate()],
      }),
    [effectiveChartWidth, axisRange]
  );

  const domainStartMs = axisRange.start.toMillis();
  const domainEndMs = axisRange.end.toMillis();
  const domainStartX = dateScale(axisRange.start.toJSDate()) ?? CHART_PADDING_X;
  const domainEndX =
    dateScale(axisRange.end.toJSDate()) ??
    effectiveChartWidth - CHART_PADDING_X;

  const todayMs =
    today >= axisRange.start && today <= axisRange.end
      ? today.toMillis()
      : null;
  const todayX = todayMs !== null ? (dateScale(new Date(todayMs)) ?? 0) : null;
  const hoveredX =
    hoveredMs !== null ? (dateScale(new Date(hoveredMs)) ?? 0) : null;

  const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = chartColumnRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const hoveredDate = dateScale.invert(localX);
    const hovered = hoveredDate.getTime();
    if (hovered < domainStartMs) {
      setHoveredMs(domainStartMs);
    } else if (hovered > domainEndMs) {
      setHoveredMs(domainEndMs);
    } else {
      setHoveredMs(hovered);
    }
  };

  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DemandScheduleLegend />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box
            sx={{
              width: GUTTER_WIDTH,
              flex: `0 0 ${String(GUTTER_WIDTH)}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: `${String(ROW_GAP)}px`,
            }}
          >
            {rows.map((row) => (
              <RowGutter key={row.sku} row={row} />
            ))}
            <Box sx={{ height: AXIS_HEIGHT }} />
          </Box>

          <Box
            ref={chartColumnRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredMs(null)}
            sx={{
              flex: 1,
              minWidth: CHART_MIN_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              gap: `${String(ROW_GAP)}px`,
            }}
          >
            {chartWidth > 0 && (
              <>
                {rows.map((row) => (
                  <DemandScheduleRowChart
                    key={row.sku}
                    row={row}
                    width={effectiveChartWidth}
                    dateScale={dateScale}
                    domainStartX={domainStartX}
                    domainEndX={domainEndX}
                    hoveredMs={hoveredMs}
                    hoveredX={hoveredX}
                    todayX={todayX}
                  />
                ))}
                <SharedTimeAxis
                  width={effectiveChartWidth}
                  dateScale={dateScale}
                  start={axisRange.start}
                  end={axisRange.end}
                  tickUnit={tickUnit}
                />
              </>
            )}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

function DemandScheduleLegend() {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <LegendSwatch
        fill={alpha(theme.palette.primary.main, 0.16)}
        border={theme.palette.primary.main}
        label="Dated demand"
      />
      <LegendSwatch
        patterned
        border={theme.palette.text.disabled}
        label="Baseline"
      />
      <LegendSwatch dashed border={theme.palette.primary.light} label="Today" />
    </Stack>
  );
}

interface LegendSwatchProps {
  label: string;
  fill?: string;
  border?: string;
  patterned?: boolean;
  dashed?: boolean;
}

function LegendSwatch({
  label,
  fill,
  border,
  patterned,
  dashed,
}: LegendSwatchProps) {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 20,
          height: 10,
          borderRadius: 0.5,
          border: border
            ? `1px ${dashed ? 'dashed' : 'solid'} ${border}`
            : undefined,
          backgroundColor: fill ?? 'transparent',
          backgroundImage: patterned
            ? `repeating-linear-gradient(45deg, ${alpha(
                theme.palette.text.disabled,
                0.6
              )} 0 1px, transparent 1px 4px)`
            : undefined,
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

interface RowGutterProps {
  row: DemandScheduleRow;
}

function RowGutter({ row }: RowGutterProps) {
  return (
    <Box
      sx={{
        height: ROW_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        paddingRight: 1,
      }}
    >
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ fontFamily: 'monospace' }}
      >
        {row.sku}
      </Typography>
      {row.itemName && (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={row.itemName}
        >
          {row.itemName}
        </Typography>
      )}
      <Typography variant="body2" color="text.primary">
        Peak:{' '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {formatNumber(row.peak)}
        </Box>
      </Typography>
    </Box>
  );
}

interface DemandScheduleRowChartProps {
  row: DemandScheduleRow;
  width: number;
  dateScale: DateScale;
  domainStartX: number;
  domainEndX: number;
  hoveredMs: number | null;
  hoveredX: number | null;
  todayX: number | null;
}

function DemandScheduleRowChart({
  row,
  width,
  dateScale,
  domainStartX,
  domainEndX,
  hoveredMs,
  hoveredX,
  todayX,
}: DemandScheduleRowChartProps) {
  const theme = useTheme();

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [ROW_HEIGHT, 0],
        domain: [0, row.peak > 0 ? row.peak : 1],
      }),
    [row.peak]
  );

  const stepPaths = useMemo(
    () =>
      buildStepPaths(
        row,
        dateScale,
        yScale,
        domainStartX,
        domainEndX,
        ROW_HEIGHT
      ),
    [row, dateScale, yScale, domainStartX, domainEndX]
  );

  const baselineY = yScale(row.baseline) ?? ROW_HEIGHT;
  const baselineHeight = Math.max(0, ROW_HEIGHT - baselineY);
  const baselineWidth = Math.max(0, domainEndX - domainStartX);
  const hoveredValue = hoveredMs !== null ? valueAtTime(row, hoveredMs) : null;
  const patternId = `demand-baseline-${row.sku.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

  return (
    <Box sx={{ width: '100%' }}>
      <svg
        width={width}
        height={ROW_HEIGHT}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <PatternLines
            id={patternId}
            height={5}
            width={5}
            strokeWidth={1}
            stroke={alpha(theme.palette.text.disabled, 0.6)}
            background={alpha(theme.palette.text.disabled, 0.1)}
            orientation={['diagonal']}
          />
        </defs>

        {row.baseline > 0 && (
          <rect
            x={domainStartX}
            y={baselineY}
            width={baselineWidth}
            height={baselineHeight}
            fill={`url(#${patternId})`}
          />
        )}

        {stepPaths && (
          <>
            <path
              d={stepPaths.area}
              fill={alpha(theme.palette.primary.main, 0.16)}
            />
            <path
              d={stepPaths.line}
              fill="none"
              stroke={theme.palette.primary.main}
              strokeWidth={1.5}
            />
          </>
        )}

        {todayX !== null && (
          <Line
            from={{ x: todayX, y: 0 }}
            to={{ x: todayX, y: ROW_HEIGHT }}
            stroke={theme.palette.primary.light}
            strokeWidth={1}
            strokeDasharray="4 2"
          />
        )}

        {hoveredX !== null && hoveredValue !== null && (
          <>
            <Line
              from={{ x: hoveredX, y: 0 }}
              to={{ x: hoveredX, y: ROW_HEIGHT }}
              stroke={theme.palette.text.secondary}
              strokeWidth={1}
            />
            <rect
              x={hoveredX + 4}
              y={2}
              width={36}
              height={14}
              fill={theme.palette.background.paper}
              stroke={theme.palette.divider}
              strokeWidth={0.5}
              rx={2}
            />
            <text
              x={hoveredX + 8}
              y={12}
              fontSize="10"
              fill={theme.palette.text.primary}
            >
              {formatNumber(hoveredValue)}
            </text>
          </>
        )}
      </svg>
    </Box>
  );
}

function valueAtTime(row: DemandScheduleRow, timeMs: number): number {
  let datedQuantity = 0;
  for (const step of row.steps) {
    if (step.timeMs <= timeMs) {
      datedQuantity = step.datedQuantity;
    } else {
      break;
    }
  }
  return row.baseline + datedQuantity;
}

type TickUnit = 'month' | 'quarter' | 'year';

function pickTickUnit(start: DateTime, end: DateTime): TickUnit {
  const diffMonths = end.diff(start, 'months').months;
  if (diffMonths <= 12) return 'month';
  if (diffMonths <= 36) return 'quarter';
  return 'year';
}

interface SharedTimeAxisProps {
  width: number;
  dateScale: DateScale;
  start: DateTime;
  end: DateTime;
  tickUnit: TickUnit;
}

function SharedTimeAxis({
  width,
  dateScale,
  start,
  end,
  tickUnit,
}: SharedTimeAxisProps) {
  const theme = useTheme();
  const rawTicks = generateAxisTicks(start, end, tickUnit);

  // Cap visible ticks so narrow layouts don't overflow their labels.
  const stride = Math.max(1, Math.ceil(rawTicks.length / MAX_TICKS));
  const ticks = rawTicks.filter((_, index) => index % stride === 0);

  const formatTick = (date: DateTime) => {
    if (tickUnit === 'year') return date.toFormat('yyyy');
    return date.toFormat("LLL ''yy");
  };

  return (
    <svg
      width={width}
      height={AXIS_HEIGHT}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <Line
        from={{ x: 0, y: 0 }}
        to={{ x: width, y: 0 }}
        stroke={theme.palette.divider}
        strokeWidth={1}
      />
      {ticks.map((tick) => {
        const x = dateScale(tick.toJSDate()) ?? 0;
        if (x < CHART_PADDING_X || x > width - CHART_PADDING_X) return null;
        return (
          <g key={tick.toISO()}>
            <Line
              from={{ x, y: 0 }}
              to={{ x, y: 4 }}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            <text
              x={x}
              y={18}
              fontSize="10"
              fill={theme.palette.text.secondary}
              textAnchor="middle"
            >
              {formatTick(tick)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function generateAxisTicks(
  start: DateTime,
  end: DateTime,
  unit: TickUnit
): DateTime[] {
  // Ticks snap to clean unit boundaries (1st of month / quarter / year) so
  // mid-month ranges still read as "May '26, Jun '26, ..." instead of the
  // label landing on the project's exact start date.
  const boundary = (dt: DateTime): DateTime => {
    if (unit === 'year') return dt.startOf('year');
    if (unit === 'quarter') return dt.startOf('quarter');
    return dt.startOf('month');
  };
  const step: Parameters<DateTime['plus']>[0] =
    unit === 'year'
      ? { years: 1 }
      : unit === 'quarter'
        ? { months: 3 }
        : { months: 1 };

  let current = boundary(start);
  if (current < start) current = current.plus(step);

  const ticks: DateTime[] = [];
  while (current <= end) {
    ticks.push(current);
    current = current.plus(step);
  }
  return ticks;
}
